import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ConsentLayout } from './ConsentLayout';
import { clearConsentDraft, getConsentDraft } from '../../../lib/consent/consentDraft';

/**
 * Step 7 — confirmation screen.
 *
 * Shown after commit-parental-consent succeeds. Tells the parent
 * what's happened + what's next. Clears the consent draft so a fresh
 * tab won't restart the flow.
 *
 * Wave 3.4 will refine the "what happens next" section once child
 * account creation is wired.
 */
export function ConsentConfirmation() {
  const [parentEmail, setParentEmail] = useState<string | null>(null);
  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    const draft = getConsentDraft();
    setParentEmail(draft?.parentEmail ?? null);
    const c = sessionStorage.getItem('4cm_consent_count');
    setCount(c ? parseInt(c, 10) : 0);
    // Clear the draft so a refresh / re-open doesn't loop the flow.
    clearConsentDraft();
    sessionStorage.removeItem('4cm_consent_verified');
    sessionStorage.removeItem('4cm_consent_parent_user_id');
    sessionStorage.removeItem('4cm_consent_count');
  }, []);

  return (
    <ConsentLayout
      step={7}
      title="All set"
      lead={
        <>
          We've recorded {count > 0 ? <strong>{count}</strong> : 'your'} consent {count === 1 ? 'choice' : 'choices'}
          {parentEmail ? <> for <strong>{parentEmail}</strong></> : null}. Thank you.
        </>
      }
    >
      <div className="consent-status consent-status--success">
        <h2 className="consent-status__heading">What happens next</h2>
        <ol className="consent-status__steps">
          <li>
            We just sent you an email with a link to set your password. Use
            it to sign into your parent account on 4CM.
          </li>
          <li>
            After you sign in, head to <Link to="/parent/child/setup">Set up your child's account</Link>{' '}
            to give your child their own email + password. They'll receive a
            confirmation email so they can sign in.
          </li>
          <li>
            You can come back to <Link to="/legal/privacy">our privacy notice</Link>{' '}
            any time. From your parent account you'll be able to change any
            of these consents in Settings → Privacy.
          </li>
        </ol>
      </div>

      <div className="consent-form__actions">
        <Link to="/" className="consent-form__btn">
          Back to 4CM
        </Link>
      </div>
    </ConsentLayout>
  );
}
